import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import StaffDashboard from './pages/StaffDashboard';
import TeamPage from './pages/TeamPage';
import ReconciliationPage from './pages/ReconciliationPage';
import SSOPage from './pages/SSOPage';
import ClientPortal from './pages/ClientPortal';
import PrintPreviewPage from './pages/PrintPreviewPage';
import PrintPreviewConsolidatedPage from './pages/PrintPreviewConsolidatedPage';
import { TermsPage, PrivacyPage } from './pages/LegalPage';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Protected route component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F0F5F8',
      }}>
        <Loader2 size={32} className="animate-spin" color="#0082B0" />
      </div>
    );
  }
  
  if (!user) {
    // SSO mode bounce: deployed under clientfinancials.steelemartin.com,
    // there's no in-app login screen — users sign in at the Employee
    // Portal. Hand them off there.
    const portalUrl = process.env.REACT_APP_PORTAL_URL;
    if (portalUrl) {
      window.location.href = `${portalUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'client') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Home redirect component
function HomeRedirect() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F0F5F8',
      }}>
        <Loader2 size={32} className="animate-spin" color="#0082B0" />
      </div>
    );
  }
  
  if (!user) {
    const portalUrl = process.env.REACT_APP_PORTAL_URL;
    if (portalUrl) {
      window.location.href = `${portalUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === 'client') {
    return <Navigate to="/portal" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso" element={<SSOPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      {/* V16 Pass D — Headless print preview route consumed by the
          Playwright subprocess of POST /api/pdf/generate. Auth is via
          render-token (POST /api/pdf/render-token/consume), NOT user JWT,
          so this route stays PUBLIC. */}
      <Route path="/print-preview/:clientId" element={<PrintPreviewPage />} />
      <Route path="/print-preview-consolidated/:groupId" element={<PrintPreviewConsolidatedPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['staff', 'admin']}>
            <StaffDashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recon"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ReconciliationPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/portal" 
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientPortal />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
